import { type FC, type ComponentProps } from 'react';
import {
    RelayEnvironmentProvider,
    type Environment,
    type GraphQLTaggedNode,
    useLazyLoadQuery,
    fetchQuery,
} from 'react-relay';
import { type OperationType, type VariablesOf } from 'relay-runtime';
import { IntlProvider } from 'dibs-react-intl';
import { ToastProvider } from 'dibs-toaster/exports/ToastProvider.ts';

import { Error500Layout } from 'dibs-buyer-layout/exports/error500.tsx';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';

import { SocketContextProvider } from 'dibs-subscriptions-utils/exports/SocketContext.ts';
import { type SocketListeners } from 'dibs-subscriptions-utils/exports/types.ts';

type IntlProviderProps = ComponentProps<typeof IntlProvider>;
type Locale = IntlProviderProps['locale'];
type Messages = IntlProviderProps['messages'];

type DefaultProps = { viewer: unknown };
type PropsWithoutViewer<P extends DefaultProps> = Omit<P, 'viewer'>;
type FunctionComponentWithoutViewer<P extends DefaultProps> = FC<PropsWithoutViewer<P>>;

export function layoutEntry<P extends DefaultProps, TQuery extends OperationType>(
    Layout: FC<P>,
    query: GraphQLTaggedNode
): (options: {
    environment: Environment;
    socketListeners?: SocketListeners;
    relayVars: VariablesOf<TQuery>;
    executeFetchQuery?: boolean;
    locale: Locale;
    messages: Messages;
}) => Promise<{
    data: TQuery['response'] | null;
    Layout: FunctionComponentWithoutViewer<P>;
}> {
    return async ({
        environment,
        socketListeners = null,
        relayVars,
        executeFetchQuery,
        locale,
        messages,
    }) => {
        const fetchQueryData = executeFetchQuery
            ? await fetchQuery<TQuery>(environment, query, relayVars).toPromise()
            : null;

        const LayoutLazyLoadQuery: FunctionComponentWithoutViewer<P> = props => {
            const data = useLazyLoadQuery(query, relayVars, {
                fetchPolicy: 'store-only',
            });

            return data && typeof data === 'object' && 'viewer' in data ? (
                <Layout {...(props as P)} viewer={data.viewer} />
            ) : null;
        };

        const LayoutEntry: FunctionComponentWithoutViewer<P> = props => {
            return (
                <RelayEnvironmentProvider environment={environment}>
                    <SocketContextProvider socketListeners={socketListeners}>
                        <IntlProvider locale={locale} messages={messages}>
                            <ErrorBoundary
                                fallback={({ error }) => <Error500Layout error={error} />}
                            >
                                <ToastProvider>
                                    <LayoutLazyLoadQuery {...props} />
                                </ToastProvider>
                            </ErrorBoundary>
                        </IntlProvider>
                    </SocketContextProvider>
                </RelayEnvironmentProvider>
            );
        };

        return {
            data: fetchQueryData,
            Layout: LayoutEntry,
        };
    };
}
